import { Mail, MapPin, MessageCircle, Phone } from "lucide-react";
import { SiteLayout } from "@/components/layout/SiteLayout";
import { InquiryForm } from "@/components/InquiryForm";
import { ContactMap } from "@/components/contact/ContactMap";
import { indiaMapLinkUrl } from "@/lib/indiaContact";
import { PageHero } from "@/components/ui";
import { SocialLinks } from "@/components/layout/SocialLinks";
import { INDIA_OFFICE, indiaWhatsAppUrl } from "@/lib/indiaContact";
import { Seo } from "@/lib/seo/Seo";

const WHATSAPP_URL = indiaWhatsAppUrl();

const SIDEBAR_CARD = "rounded-2xl border border-brand-200 bg-white p-4";
const SIDEBAR_ICON =
  "flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-brand-100 text-brand-600";
const SIDEBAR_ROW =
  "flex items-center gap-3 rounded-xl border border-brand-200 px-3 py-2.5 transition-colors hover:border-brand-300 hover:bg-brand-50/80";

export default function Contact() {
  return (
    <SiteLayout>
      <Seo
        title="Contact Us | A Learning Lab India"
        description="Contact the A Learning Lab India advisory team in Gurgaon by phone, WhatsApp or message about virtual internships, bootcamps and programmes."
        path="/contact"
      />
      <div data-testid="contact-page" className="page-main">
        <PageHero
          testId="contact-hero"
          icon={Mail}
          eyebrow="We're here for your questions"
          title="Let's Talk"
          introLines={[
            "Get comprehensive guidance to find the right learning opportunities for your child",
            "and move forward with confidence.",
          ]}
        />

        <section className="bg-brand-50 py-12 sm:py-16 lg:py-20">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div
              className="grid min-h-0 overflow-hidden rounded-3xl border border-brand-200 bg-white shadow-soft lg:min-h-[560px] lg:grid-cols-[minmax(17.5rem,21.25rem)_minmax(0,1fr)]"
              data-testid="contact-panel"
            >
              <aside className="flex flex-col gap-3 border-b border-brand-200 bg-brand-50 p-5 sm:p-6 lg:border-b-0 lg:border-r lg:p-7">
                <div className={`${SIDEBAR_CARD} space-y-3`}>
                  <p className="text-[10px] font-bold uppercase tracking-wider text-brand-600">Call or WhatsApp</p>
                  <a
                    data-testid="contact-phone"
                    href={`tel:${INDIA_OFFICE.tel}`}
                    className={SIDEBAR_ROW}
                  >
                    <span className={SIDEBAR_ICON}>
                      <Phone className="h-4 w-4" aria-hidden="true" />
                    </span>
                    <span className="min-w-0">
                      <span className="block text-xs font-bold text-ink">Call us</span>
                      <span className="block truncate text-sm font-semibold text-ink/80">{INDIA_OFFICE.phone}</span>
                    </span>
                  </a>
                  <a
                    data-testid="contact-whatsapp"
                    href={WHATSAPP_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={SIDEBAR_ROW}
                  >
                    <span className={SIDEBAR_ICON}>
                      <MessageCircle className="h-4 w-4" aria-hidden="true" />
                    </span>
                    <span className="min-w-0">
                      <span className="block text-xs font-bold text-ink">WhatsApp</span>
                      <span className="block text-[11px] leading-snug text-ink/60">
                        Fastest reply — usually within minutes
                      </span>
                    </span>
                  </a>
                </div>

                <div data-testid="contact-address" className={SIDEBAR_CARD}>
                  <div className="flex items-start gap-3">
                    <span className={`${SIDEBAR_ICON} mt-0.5`}>
                      <MapPin className="h-4 w-4" aria-hidden="true" />
                    </span>
                    <div className="min-w-0">
                      <p className="text-xs font-bold text-ink">Get in touch</p>
                      <address className="mt-1 not-italic text-[11px] leading-relaxed text-ink/70">
                        {INDIA_OFFICE.address}
                      </address>
                      <a
                        href={indiaMapLinkUrl()}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="mt-1.5 inline-flex min-h-[44px] items-center text-xs font-bold text-brand-600 hover:text-brand-800"
                        data-testid="contact-sidebar-directions"
                      >
                        Get directions
                        <span className="sr-only"> (opens in a new tab)</span>
                      </a>
                    </div>
                  </div>
                </div>

                <a
                  href="mailto:info@alearninglab.com"
                  className={`${SIDEBAR_CARD} flex items-center gap-3 hover:border-brand-300`}
                >
                  <span className={SIDEBAR_ICON}>
                    <Mail className="h-4 w-4" aria-hidden="true" />
                  </span>
                  <div className="min-w-0">
                    <p className="text-xs font-bold text-ink">Email</p>
                    <p className="mt-0.5 text-[11px] text-ink/70">info@alearninglab.com</p>
                  </div>
                </a>

                <div className={`${SIDEBAR_CARD} mt-auto`}>
                  <p className="mb-3 text-xs font-bold text-ink">Follow us</p>
                  <SocialLinks variant="light" className="gap-2" />
                </div>
              </aside>

              <div className="flex flex-col bg-white p-6 sm:p-8 lg:px-10 lg:py-9">
                <p className="text-[10px] font-bold uppercase tracking-wider text-brand-600">Send a message</p>
                <h2 className="mt-1 mb-6 font-heading text-2xl font-bold leading-tight text-ink lg:mb-8">
                  Tell us how we can help
                </h2>
                <InquiryForm layout="contact" />
              </div>
            </div>
          </div>
        </section>

        <ContactMap />
      </div>
    </SiteLayout>
  );
}
